/** @file Orders bones parents-first, assigns indices and emits export bone records (incl. socket bones). */
import { throwError } from '@pose2d/format/throwError';
import type { BoneDoc, Doc, DocIndex, Pose2DJson } from '@pose2d/format/types';
import { COMPILE_CODES } from './compileCodes';

type BoneJson = Pose2DJson['bones'][number];

function boneRecord(bone: BoneDoc, parent: number | undefined): BoneJson {
  const s = bone.setup;
  return {
    name: bone.name,
    ...(parent === undefined ? {} : { parent }),
    length: bone.length,
    inherit: COMPILE_CODES.inherit[bone.inherit],
    ...(bone.kind === undefined ? {} : { kind: bone.kind }),
    x: s.x, y: s.y, rotation: s.rotation, scaleX: s.scaleX, scaleY: s.scaleY, shearX: s.shearX, shearY: s.shearY,
  };
}

/** A bone no root reaches: its parent chain hits a missing bone (NOT_FOUND) or loops (CYCLE). */
function throwUnreachable(doc: Doc, id: string): never {
  const seen: string[] = [];
  let prev = id;
  let cur: string | null = id;
  while (cur !== null && !seen.includes(cur)) {
    const bone: BoneDoc | undefined = doc.bones[cur];
    if (bone === undefined) throwError('NOT_FOUND', { kind: 'bone', ref: cur }, { path: `bones.${prev}.parent` });
    seen.push(cur);
    prev = cur;
    cur = bone.parent;
  }
  const loop = [...seen, cur ?? id];
  const names = loop.map((b) => doc.bones[b]?.name ?? b).join(' -> ');
  return throwError('CYCLE', { path: loop.join(' -> ') }, { path: `bones.${id}.parent`, hint: `the parent chain loops: ${names}` });
}

/** Returns bone records and the id -> index map. */
export function compileBones(doc: Doc, index: DocIndex): { bones: Pose2DJson['bones']; boneIndex: Map<string, number> } {
  const bones: Pose2DJson['bones'] = [];
  const boneIndex = new Map<string, number>();
  const stack = Object.keys(doc.bones).filter((id) => doc.bones[id]?.parent === null).reverse();
  for (let id = stack.pop(); id !== undefined; id = stack.pop()) {
    const bone = doc.bones[id];
    if (bone === undefined || boneIndex.has(id)) continue;
    boneIndex.set(id, bones.length);
    bones.push(boneRecord(bone, bone.parent === null ? undefined : boneIndex.get(bone.parent)));
    stack.push(...[...(index.children.get(id) ?? [])].reverse());
  }
  const lost = Object.keys(doc.bones).find((id) => !boneIndex.has(id));
  if (lost !== undefined) throwUnreachable(doc, lost);
  return { bones, boneIndex };
}
